import { BrainFocusApi } from '@brainfocus/web/brainfocus_api';
import { useNavigate } from '@brainfocus/web/router/use_navigate';
import { getPrivacyVersion, getTocVersion } from '@brainfocus/web/services/legal_versions';
import { markAcceptanceSatisfied } from '@brainfocus/web/services/acceptance';
import { useState } from 'preact/hooks';

/**
 * Re-acceptance page (#555). The login gate redirects any authenticated
 * user whose ``toc_version``/``privacy_version`` does not match the active
 * constants here, with the originally requested path in ``?next=``. After
 * the user accepts, we POST to ``/api/accept-terms`` and navigate to
 * ``next`` (or ``/``).
 */
export function AcceptTerms() {
    const navigate = useNavigate();
    const tocVersion = getTocVersion();
    const privacyVersion = getPrivacyVersion();

    const [accepted, setAccepted] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const next = new URLSearchParams(window.location.search).get('next') || '/';

    async function handleSubmit(e: Event) {
        e.preventDefault();
        if (!accepted || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await BrainFocusApi.acceptTerms();
            // The server-rendered bf-needs-acceptance meta is now stale (still
            // "true"); mark acceptance satisfied so the proactive gate check
            // does not bounce the navigation to `next` back here (#756).
            markAcceptanceSatisfied();
            await navigate(next);
        } catch (err: any) {
            setError(err?.apiError || err?.message || 'Failed to record acceptance');
            setSubmitting(false);
        }
    }

    return (
        <div class='bf-ui-content'>
            <div class='container'>
                <div
                    style={{
                        maxWidth: '560px',
                        margin: '48px auto',
                        padding: '32px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        background: '#fff',
                        textAlign: 'left',
                    }}
                >
                    <h1 style={{ fontSize: '22px', fontWeight: 600, marginBottom: '16px' }}>
                        Updated Terms &amp; Privacy Policy
                    </h1>
                    <p style={{ marginBottom: '24px' }}>
                        To continue using BrainFocus you must review and accept the current legal documents.
                    </p>

                    <div style={{ marginBottom: '8px' }}>
                        <strong>Terms of Service</strong>
                        <span>{` — version ${tocVersion} · `}</span>
                        <a href='/terms-of-service' target='_blank' rel='noopener noreferrer'>
                            Read
                        </a>
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                        <strong>Privacy Policy</strong>
                        <span>{` — version ${privacyVersion} · `}</span>
                        <a href='/privacy-policy' target='_blank' rel='noopener noreferrer'>
                            Read
                        </a>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <label
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px',
                                marginBottom: '20px',
                            }}
                        >
                            <input
                                type='checkbox'
                                name='accept'
                                checked={accepted}
                                onChange={e => setAccepted((e.target as HTMLInputElement).checked)}
                                style={{ marginTop: '3px', flexShrink: 0 }}
                            />
                            <span>I have read and accept the Terms of Service and the Privacy Policy.</span>
                        </label>
                        <button class='bf-action' type='submit' disabled={!accepted || submitting}>
                            {submitting ? 'Saving...' : 'Accept'}
                        </button>
                        {error && (
                            <p class='cl-error' style={{ marginTop: '12px' }}>
                                {error}
                            </p>
                        )}
                    </form>

                    <p style={{ marginTop: '24px' }}>
                        <a href='/logout'>Log out instead</a>
                    </p>
                </div>
            </div>
        </div>
    );
}
